import {
    ApolloError,
    ApolloLink,
    ApolloQueryResult,
    createHttpLink,
    OperationVariables,
    QueryOptions,
} from "@apollo/client";
import { MutationOptions } from "@apollo/client/core/watchQueryOptions";
import { FetchResult } from "@apollo/client/link/core";
import { withScalars } from "apollo-link-scalars";
import * as $ from "jquery";
import { graphQLClient, GraphQLErrors } from "~/common/graphql/clients";
import { GraphQLServerError } from "~/common/graphql/GraphQLServerError";
import { OAuthTokenRenewer } from "~/EasyProjectsCommon/OAuthTokenRenewer";
import { getSessionIdHeader } from "~/EasyProjectsCommon/Session";
import { typesMap } from "../common/graphql/schemas/scalarTypes";
import { schema } from "../common/graphql/schemas/schema";
import "./Ajax";
import { appUrls } from "./AppUrls";
import { dateUtility } from "./Dates/DateUtility";
import { XhrDeferredType } from "./General";
import { LSHelper } from "./LSHelper";
import { createUpload } from "./UploadFile";

export interface Token {
    access_token: string;
    token_type: string;
    expires_in: number;
}

export interface StorageToken {
    access_token: string;
    expireDate: string;
    token_type: string;
    userId: number;
}

export const OAuthClientTokenKey = "OAuthClientToken";
const UnauthorizedErrorCode = 401;

type AbortableXhrDeferredType = JQuery.Promise<any> & {
    abort(): void;
};

export class OAuthClient {
    private static _tokenPromise: JQuery.Promise<StorageToken>;

    public static reset() {
        LSHelper.remove(OAuthClientTokenKey);
    }

    public static postJson<TData>(
        url: string,
        data?: TData,
        optionalParams?: Partial<OptionalParams>
    ): AbortableXhrDeferredType {
        return OAuthClient.do(params => ep.common.postJson(url, data as any, null, params), optionalParams);
    }

    public static get(url: string, optionalParams?: Partial<OptionalParams>): AbortableXhrDeferredType {
        return OAuthClient.do(params => ep.common.send("GET", url, null, null, params), optionalParams);
    }

    public static put(url: string, data?: any, optionalParams?: Partial<OptionalParams>): AbortableXhrDeferredType {
        return OAuthClient.do(params => ep.common.send("PUT", url, data, null, params), optionalParams);
    }

    public static delete(url: string, data?: any, optionalParams?: Partial<OptionalParams>): AbortableXhrDeferredType {
        //request body(data) is not supported for DELETE method, it is truncated by our ingress proxy
        return OAuthClient.do(params => ep.common.send("DELETE", url, data, null, params), optionalParams);
    }

    private static do(
        exec: F1<Partial<OptionalParams>, XhrDeferredType>,
        optionalParams?: Partial<OptionalParams>
    ): AbortableXhrDeferredType {
        let xhr: XhrDeferredType = $.Deferred().resolve() as XhrDeferredType;
        let isAborted = false;

        const result = this._sendWithRetryOnUnauthorized(params => {
            xhr = isAborted ? ($.Deferred().resolve() as XhrDeferredType) : exec(params);
            return xhr;
        }, optionalParams) as unknown as AbortableXhrDeferredType;

        result.abort = () => {
            isAborted = true;

            if (xhr?.abort) {
                xhr.abort();
            }
        };

        return result;
    }

    public static async graphql<T = any, TVariables = OperationVariables>(
        endpoint: string,
        options: QueryOptions<TVariables, T> | MutationOptions<T, TVariables>
    ) {
        try {
            return await this.graphqlInternal(endpoint, options);
        } catch (err) {
            throw new GraphQLServerError(err, endpoint);
        }
    }

    static async graphqlInternal<T = any, TVariables = OperationVariables>(
        endpoint: string,
        options: QueryOptions<TVariables, T> | MutationOptions<T, TVariables>,
        retryOnUnauthorized = true
    ): Promise<ApolloQueryResult<T> | FetchResult<T>> {
        const endpointLink = createHttpLink({
            uri: endpoint,
            headers: {
                ...this._getAuthorizationHeaders(await this.getToken()),
                ...getSessionIdHeader(),
                "X-TimeZoneOffset": -new Date().getTimezoneOffset() + "",
            },
        });

        const finalLink = ApolloLink.from([withScalars({ schema, typesMap }), endpointLink]);
        graphQLClient.setLink(finalLink);

        try {
            if ("query" in options) {
                let result = await graphQLClient.query(options);
                return result;
            }
            if ("mutation" in options) {
                let result = await graphQLClient.mutate(options);
                return result;
            }

            throw new Error("Unsupported options");
        } catch (err) {
            if (isUnauthorizedError(err)) {
                this.reset();

                if (retryOnUnauthorized) {
                    return await this.graphqlInternal(endpoint, options, false);
                }
            }

            throw err;
        }
    }

    public static downloadFile(url: string, data: any, filename?: string, onLoadCompleted?: F) {
        return this._sendWithRetryOnUnauthorized(
            params => ep.common.downloadFile(url, data, filename, onLoadCompleted, params) as XhrDeferredType
        );
    }

    public static uploadFile(url: string, file: File, dataFormatter?: F1<Dictionary<any>, Dictionary<any>>) {
        return this.getToken().then((token: StorageToken) => {
            const headers = this._getAuthorizationHeaders(token);

            const result = $.Deferred();

            createUpload({ endpoint: url, file, headers, dataFormatter }).then(
                (x: any) => {
                    result.resolve(x);
                },
                (x: any) => {
                    result.reject(x);
                }
            );

            return result.promise();
        });
    }

    public static postMultipart(url: string, data?: any, optionalParams?: Partial<OptionalParams>) {
        return this._sendWithRetryOnUnauthorized(params => ep.common.postMultipart(url, data, params), optionalParams);
    }

    public static getCurrentDate() {
        return new Date();
    }

    public static getToken() {
        const existingToken: StorageToken = LSHelper.getJSON(OAuthClientTokenKey);

        if (existingToken && existingToken.userId === ep.settings.currentUserId) {
            if (dateUtility.strictLessOrEqual(this.getCurrentDate(), new Date(existingToken.expireDate))) {
                return $.Deferred<StorageToken>().resolve(existingToken).promise();
            }
        }

        if (!this._tokenPromise) {
            this._tokenPromise = this._sendGetTokenRequest().always(() => (this._tokenPromise = null));
        }

        return this._tokenPromise;
    }

    private static _handleUnauthorizedResponse<T extends XhrDeferredType>(promise: T) {
        promise.fail(request => {
            if (request && request.status === UnauthorizedErrorCode) {
                this.reset();
            }
        });
        return promise;
    }

    private static _sendGetTokenRequest() {
        OAuthTokenRenewer.onNewTokenRequesting();

        return ep.common.postJson(appUrls.common.getOauthToken).then((token: Token) => {
            // By default, we have tokens that expires in 15 minutes, treat such tokens as long-lived.
            // But we have customization InactivityLogoutTimeoutInSec, that might set the token expiration to a few minutes,
            // treat such tokens here as short-lived tokens.
            // We'll renew long-lived tokens 3 minutes before expiration, and short-lived tokens 1 minute before expiration.
            // For the short-lived tokens we specify smaller expiration threshold because otherwise we'll renew them too often.
            // For long-lived tokens it's more save to has longer expiration threshold because there is fewer chances that
            // the long-running request fails in the middle of the execution when the token will be resent between microservices.
            const isLongLivedToken = token.expires_in >= 60 * 15; // 15 minutes;
            const expirationThreshold = isLongLivedToken ? 60 * 3 : 60;

            let expiresIn = token.expires_in - expirationThreshold;

            const expireDate = dateUtility.addSeconds(this.getCurrentDate(), expiresIn);

            const data: StorageToken = {
                access_token: token.access_token,
                expireDate: expireDate.toString(),
                token_type: token.token_type,
                userId: ep.settings.currentUserId,
            };

            LSHelper.reSet(OAuthClientTokenKey, data);

            OAuthTokenRenewer.onNewTokenReceived({ autoRenew: isLongLivedToken });

            return data;
        });
    }

    private static _getAuthorizationHeaders = (token: StorageToken) =>
        ({
            Authorization: token.token_type + " " + token.access_token,
        } as const);

    private static _sendWithRetryOnUnauthorized(
        sendRequest: F1<Partial<OptionalParams>, XhrDeferredType>,
        optionalParams?: Partial<OptionalParams>
    ) {
        return this.getToken().then((token: StorageToken) => {
            const params: Partial<OptionalParams> = {
                ...optionalParams,
                headers: this._getAuthorizationHeaders(token),
                ignoreUnauthorizedError: true,
            };
            const promise = sendRequest(params);
            return promise.then(
                () => promise,
                request => {
                    if (request && request.status === UnauthorizedErrorCode) {
                        this.reset();

                        return this.getToken().then(token => {
                            const params: Partial<OptionalParams> = {
                                ...optionalParams,
                                headers: this._getAuthorizationHeaders(token),
                            };
                            const newPromise = sendRequest(params);
                            return this._handleUnauthorizedResponse(newPromise);
                        });
                    }

                    return promise;
                }
            );
        });
    }
}

const isUnauthorizedError = (err: Error) =>
    err instanceof ApolloError &&
    err.graphQLErrors.some(
        e =>
            e.extensions?.code === GraphQLErrors.NotAuthenticated ||
            // TEMPORARY fix: this should be removed once the HotChocolate library is updated to correctly handle 401 errors.
            // Currently, when the auth token is expired, it incorrectly returns "AUTH_NOT_AUTHORIZED" instead of "AUTH_NOT_AUTHENTICATED".
            // -->
            e.extensions?.code === "AUTH_NOT_AUTHORIZED"
        // <--
    );
